import React, { useState, useRef, useEffect } from 'react';
import './AIChatbot.css';

const AIChatbot = ({ 
  isOpen, 
  onClose, 
  apiBaseUrl = 'http://localhost:8001/api',
  theme = 'light' 
}) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState(null);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [inputMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() && !selectedImage) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      let response;
      
      if (selectedImage) {
        // Send image message
        const formData = new FormData();
        formData.append('image', selectedImage);
        if (inputMessage.trim()) {
          formData.append('message', inputMessage.trim());
        }
        if (conversationId) {
          formData.append('conversationId', conversationId);
        }

        response = await fetch(`${apiBaseUrl}/chat/image`, {
          method: 'POST',
          body: formData,
        });
      } else {
        // Send text message
        response = await fetch(`${apiBaseUrl}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: inputMessage.trim(),
            conversationId: conversationId
          }),
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Add user message
      const userMessage = {
        id: Date.now() + '_user',
        role: 'user',
        content: inputMessage.trim() || '[Image uploaded]',
        timestamp: new Date(),
        image: imagePreview
      };
      
      // Add AI response
      const aiMessage = {
        id: Date.now() + '_ai',
        role: 'assistant',
        content: data.response,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, userMessage, aiMessage]);
      setConversationId(data.conversationId);
      
      // Clear input and image
      setInputMessage('');
      setSelectedImage(null);
      setImagePreview(null);
      
    } catch (err) {
      console.error('Error sending message:', err);
      
      // Show construction message when server is not available
      const constructionMessage = {
        id: Date.now() + '_construction',
        role: 'assistant',
        content: `🚧 **SERVER UNDER CONSTRUCTION** 🚧

The AI chatbot backend is currently being built! 🏗️

**What's happening:**
• Server is not responding (likely not running)
• Advanced AI features are still in development
• Full functionality coming very soon!

**Current Status:**
🔧 Backend Infrastructure: Building...
🤖 AI Service Integration: In Progress...
⚡ Server Deployment: Coming Soon...

**What you can expect when ready:**
• Instant responses to Computer Engineering questions
• Image analysis for circuits and diagrams  
• Code explanations and debugging help
• Comprehensive technical knowledge base

**Thank you for your patience!** 
We're working hard to bring you an amazing AI assistant! 🚀

*Try again in a few moments or check back soon!* ✨`,
        timestamp: new Date()
      };
      
      // Add user message and construction response
      const userMessage = {
        id: Date.now() + '_user',
        role: 'user',
        content: inputMessage.trim() || '[Image uploaded]',
        timestamp: new Date(),
        image: imagePreview
      };
      
      setMessages(prev => [...prev, userMessage, constructionMessage]);
      
      // Clear input and image
      setInputMessage('');
      setSelectedImage(null);
      setImagePreview(null);
      
      // Don't show the generic error message since we're showing construction message
      setError(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setError('Image size must be less than 10MB');
        return;
      }
      
      setSelectedImage(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const clearConversation = async () => {
    if (conversationId) {
      try {
        await fetch(`${apiBaseUrl}/conversation/${conversationId}`, {
          method: 'DELETE',
        });
      } catch (err) {
        console.error('Error clearing conversation:', err);
      }
    }
    
    setMessages([]);
    setConversationId(null);
    setError(null);
  };

  const formatMessage = (content) => {
    // Simple markdown-like formatting
    return content
      .split('\n')
      .map((line, index) => (
        <div key={index} className="message-line">
          {line}
        </div>
      ));
  };

  if (!isOpen) return null;

  return (
    <div className={`ai-chatbot-overlay ${theme}`}>
      <div className="ai-chatbot-modal">
        {/* Header */}
        <div className="ai-chatbot-header">
          <div className="header-title">
            <i className="bi bi-robot me-2"></i>
            <span>AI Engineering Assistant</span>
            <span className="status-indicator online"></span>
          </div>
          <div className="header-actions">
            <button 
              className="btn btn-sm btn-outline-secondary me-2"
              onClick={clearConversation}
              title="Clear conversation"
            >
              <i className="bi bi-trash"></i>
            </button>
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={onClose}
              title="Close chat"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="ai-chatbot-messages">
          {messages.length === 0 && (
            <div className="welcome-message construction-notice">
              <div className="construction-header">
                <div className="construction-icons">
                  🚧 🏗️ 👷‍♂️ 🚧
                </div>
                <h4 className="construction-title">
                  🚧 COMING SOON! STILL UNDER CONSTRUCTION 🚧
                </h4>
              </div>
              
              <div className="construction-art">
                <pre>{`
                    🏗️ 👷‍♂️ 🚧
                   /|\\    |    /|\\
                  / | \\   |   / | \\
                 🔨   ⚙️   🔧   ⚙️   🔨
                `}</pre>
              </div>

              <div className="features-preview">
                <h5>🎯 AI Chatbot Features in Development:</h5>
                <ul>
                  <li>🤖 Advanced Computer Engineering Assistant</li>
                  <li>💬 Interactive Q&A with Code Examples</li>
                  <li>🖼️ Image Analysis (Circuit Diagrams, Code Screenshots)</li>
                  <li>📚 Comprehensive Knowledge Base Coverage:</li>
                  <ul>
                    <li>- Algorithms & Data Structures</li>
                    <li>- Programming Languages (C++, Java, Python, JS)</li>
                    <li>- Computer Architecture & Systems</li>
                    <li>- Operating Systems & Networks</li>
                    <li>- Quality Assurance & Testing (TDD/BDD)</li>
                    <li>- Spring Boot & Frameworks</li>
                  </ul>
                </ul>
              </div>

              <div className="development-status">
                <h5>🔧 Currently Working On:</h5>
                <ul>
                  <li>✅ Backend Architecture: Complete</li>
                  <li>🔄 AI Service Integration: In Progress</li>
                  <li>🔄 Frontend Chat Interface: In Progress</li>
                  <li>� Knowledge Base: Expanding Daily</li>
                  <li>� Image Analysis: Coming Soon</li>
                </ul>
              </div>

              <div className="launch-info">
                <h5>🚀 Expected Launch: Very Soon!</h5>
                <p className="text-muted construction-message">
                  <strong>💡 Sneak Peek:</strong> "Ask me anything about Computer Engineering - 
                  from basic algorithms to advanced system design, and I'll provide 
                  detailed explanations with code examples!"
                </p>
                <p className="thank-you">
                  <em>Thank you for your patience while we build something amazing!</em> ⭐
                </p>
              </div>

              <div className="current-status">
                <p className="status-note">
                  💬 <strong>This chatbot will be your ultimate Computer Engineering companion!</strong>
                </p>
                <p className="text-muted small">
                  You can still try typing a message below - limited functionality is available!
                </p>
              </div>
            </div>
          )}
          
          {messages.map((message) => (
            <div 
              key={message.id} 
              className={`message ${message.role}`}
            >
              <div className="message-avatar">
                {message.role === 'user' ? (
                  <i className="bi bi-person-circle"></i>
                ) : (
                  <i className="bi bi-robot"></i>
                )}
              </div>
              <div className="message-content">
                {message.image && (
                  <div className="message-image">
                    <img src={message.image} alt="Uploaded" />
                  </div>
                )}
                <div className="message-text">
                  {formatMessage(message.content)}
                </div>
                <div className="message-time">
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="message assistant">
              <div className="message-avatar">
                <i className="bi bi-robot"></i>
              </div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Error Display */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show m-3" role="alert">
            {error}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setError(null)}
            ></button>
          </div>
        )}

        {/* Image Preview */}
        {imagePreview && (
          <div className="image-preview">
            <div className="image-preview-container">
              <img src={imagePreview} alt="Preview" />
              <button 
                className="btn btn-sm btn-danger image-remove"
                onClick={removeImage}
              >
                <i className="bi bi-x"></i>
              </button>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="ai-chatbot-input">
          <div className="input-group">
            <button 
              className="btn btn-outline-secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
              title="Upload image"
            >
              <i className="bi bi-image"></i>
            </button>
            
            <textarea
              ref={textareaRef}
              className="form-control"
              placeholder="Ask me about computer engineering concepts or upload an image..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              rows="1"
            />
            
            <button 
              className="btn btn-primary"
              onClick={sendMessage}
              disabled={isLoading || (!inputMessage.trim() && !selectedImage)}
            >
              {isLoading ? (
                <span className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </span>
              ) : (
                <i className="bi bi-send"></i>
              )}
            </button>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />
        </div>
      </div>
    </div>
  );
};

export default AIChatbot;
